import { useEffect, useState } from 'react'
import { getUnreadCount } from '@/api/http/notification-api'
import { getAccessToken } from '@/api/http/tokens'

export type NotificationSocketStatus = 'connecting' | 'connected' | 'reconnecting' | 'offline'

export function useNotificationSocket(enabled: boolean) {
  const [unreadCount, setUnreadCount] = useState(0)
  const [status, setStatus] = useState<NotificationSocketStatus>('offline')

  useEffect(() => {
    if (!enabled) {
      setStatus('offline')
      setUnreadCount(0)
      return
    }

    getUnreadCount().then((r) => setUnreadCount(r.unread)).catch(() => {})

    let ws: WebSocket | null = null
    let connectTimer: ReturnType<typeof setTimeout> | null = null
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null
    let closedByEffect = false
    let hasConnected = false

    const token = getAccessToken()
    if (token && location.protocol.startsWith('http')) {
      const proto = location.protocol === 'https:' ? 'wss:' : 'ws:'
      const wsHost = import.meta.env.DEV ? 'localhost:8080' : location.host
      const wsUrl = `${proto}//${wsHost}/api/v1/ws?token=${encodeURIComponent(token)}`

      const connect = () => {
        setStatus(hasConnected ? 'reconnecting' : 'connecting')
        try {
          ws = new WebSocket(wsUrl)
          ws.onopen = () => {
            hasConnected = true
            setStatus('connected')
          }
          ws.onmessage = (event) => {
            try {
              const msg = JSON.parse(event.data)
              if (msg.type === 'notification') {
                setUnreadCount((prev) => prev + 1)
              }
            } catch {
              // ignore parse errors
            }
          }
          ws.onerror = () => { ws?.close() }
          ws.onclose = () => {
            ws = null
            if (closedByEffect) return
            setStatus(hasConnected ? 'reconnecting' : 'offline')
            reconnectTimer = setTimeout(() => {
              reconnectTimer = null
              connect()
            }, 3000)
          }
        } catch {
          setStatus('offline')
        }
      }

      connectTimer = setTimeout(() => {
        connectTimer = null
        if (!closedByEffect) connect()
      }, 0)
    }

    const interval = setInterval(() => {
      getUnreadCount().then((r) => setUnreadCount(r.unread)).catch(() => {})
    }, 60000)

    return () => {
      closedByEffect = true
      clearInterval(interval)
      if (connectTimer) clearTimeout(connectTimer)
      if (reconnectTimer) clearTimeout(reconnectTimer)
      ws?.close()
    }
  }, [enabled])

  return { unreadCount, setUnreadCount, status }
}
